import { CircularLoader } from '@modules/common/components/CircularLoader';
import { FeedbackListStoreProvider } from '@modules/feedback/store/useFeedbackListStore';
import {
  useIsFeedbackListRedesignEnabled,
  useIsFeedbackRedesignEnabled,
} from '@modules/feedbackV2/common/hooks';
import { ExpandedFeedbackContent } from '@modules/feedbackV2/expanded/components/ExpandedFeedbackContent';
import { Box, Paper, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FeedbackLabel } from 'v2/core/entities/feedback';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetFeedback } from 'v2/core/services/records';
import isValidUuid from 'v2/utils/isStringValidUuid';
import Error404 from 'v2/views/components/error/error404';
import { FeedbackDetails } from '../components/FeedbackDetails';
import Empty from '../components/FeedbackList/EmptyFeedbackList';
import { trackFeedbackEvents } from '../utils';

const FeedbackDetailsPage = () => {
  const { id } = useParams<{ id: string }>();

  const isValidRecordId = id && isValidUuid(id);

  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();
  const isFeedbackListRedesignEnabled = useIsFeedbackListRedesignEnabled();

  const [selectedPredictionLabels, setSelectedPredictionLabels] = useState<
    FeedbackLabel[]
  >([]);

  const { feedback, status, error } = useGetFeedback(isValidRecordId ? id : '');

  useEffect(() => {
    trackFeedbackEvents({
      eventName: 'Feedback Page Viewed',
      feedbackID: id ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    const message = isErrorCode(error, ErrorCode.RecordNotFound) ? (
      <Stack spacing={0.5}>
        <Box>Feedback record with ID: {id} not found.</Box>
        <Box>This record either does not exist or may have been deleted.</Box>
      </Stack>
    ) : (
      'Failed to fetch feedback record. Please try again later.'
    );

    return <Error404 message={message} />;
  }

  if (!isValidRecordId) {
    return (
      <Error404 message="Invalid feedback record URL. Please check the link you used." />
    );
  }

  if (IsLoading(status)) {
    return (
      <CircularLoader
        sx={{
          mt: 30,
        }}
      />
    );
  }

  if (!feedback) {
    return <Empty />;
  }

  if (isFeedbackListRedesignEnabled) {
    return (
      <FeedbackListStoreProvider>
        <ExpandedFeedbackContent
          feedback={feedback}
          isLoading={false}
          selectedPredictionLabels={selectedPredictionLabels}
          setSelectedPredictionLabels={setSelectedPredictionLabels}
        />
      </FeedbackListStoreProvider>
    );
  }

  if (isFeedbackRedesignEnabled) {
    return (
      <FeedbackListStoreProvider>
        <FeedbackDetails feedback={feedback} />
      </FeedbackListStoreProvider>
    );
  }

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          padding: 0,
          borderRadius: 2,
          backgroundImage: 'none',
          height: 'calc(100vh - 22px)',
          overflow: 'auto',
          bgcolor: 'background.default',
        }}
      >
        <FeedbackListStoreProvider>
          <FeedbackDetails feedback={feedback} />
        </FeedbackListStoreProvider>
      </Paper>
    </>
  );
};

export default FeedbackDetailsPage;
